import { useAnalysisStore } from '@modules/analysis/store/analysisStore';
import { useGetDefaultTenantTimeRange } from '@modules/common/hooks';
import {
  decompressURLSearchParams,
  getDateTimeRangeFromTimestamp,
  isEmptyWhereClause,
} from '@modules/common/utils';
import { populatePayloadFromSearchParams } from '@modules/common/utils/populatePayloadFromSearchParams';
import { useFeedStore } from '@modules/feed/store/feedStore';
import { SearchActionType } from '@modules/feed/store/searchActionType';
import { getSearchQueryFromMetadata } from '@modules/feed/utils';
import { GetUseInSearchLinkOptions } from '@modules/feed/utils/getUseInSearchLink';
import { parseStringifiedDuration } from '@modules/feed/utils/parseStringifiedDuration';
import { useEffect } from 'react';
import { combineWhereClauses } from 'v2/core/services/queryBuilder/combineWhereClauses';
import { OperationType } from 'v2/core/services/queryBuilder/whereClause';

export const useInitSearchStoreFromSearchParams = () => {
  const dispatch = useFeedStore(({ dispatch }) => dispatch);
  const metadata = useAnalysisStore(({ metadata }) => metadata);
  const defaultTimeRange = useGetDefaultTenantTimeRange();

  useEffect(() => {
    const queryPayload: Partial<GetUseInSearchLinkOptions> = {};
    populatePayloadFromSearchParams(
      queryPayload,
      [
        'duration',
        'where',
        'from',
        'to',
        'whereClauseConfig',
        'prompt',
        'whereClause',
      ],
      decompressURLSearchParams,
    );

    if (queryPayload.duration) {
      queryPayload.duration = parseStringifiedDuration(queryPayload.duration);
      dispatch({
        type: SearchActionType.SET_QUERY,
        payload: { duration: queryPayload.duration },
      });
    }

    if (queryPayload.from && queryPayload.to) {
      queryPayload.duration = getDateTimeRangeFromTimestamp(
        parseInt(queryPayload.from, 10),
        parseInt(queryPayload.to, 10),
      );

      dispatch({
        type: SearchActionType.SET_QUERY,
        payload: {
          duration: queryPayload.duration,
        },
      });
    }

    if (queryPayload.whereClauseConfig || queryPayload.whereClause) {
      const feedWhereClause = getSearchQueryFromMetadata({
        metadata: metadata,
        getDurationField: false,
        defaultTimeRange,
      }).whereClause;

      const isFeedWhereClauseEmpty = feedWhereClause
        ? isEmptyWhereClause(feedWhereClause)
        : true;

      const queryWhereClause =
        queryPayload.whereClauseConfig?.whereClause ?? queryPayload.whereClause;

      const combinedWhereClause =
        isFeedWhereClauseEmpty || !feedWhereClause || !queryWhereClause
          ? queryWhereClause
          : combineWhereClauses(
              feedWhereClause,
              queryWhereClause,
              OperationType.AND,
            );

      dispatch({
        type: SearchActionType.SET_QUERY,
        payload: {
          whereClause: combinedWhereClause,
        },
      });
    }

    if (queryPayload.where) {
      dispatch({
        type: SearchActionType.SET_QUERY,
        payload: {
          whereClause: queryPayload.where,
        },
      });
    }

    if (queryPayload.prompt) {
      dispatch({
        type: SearchActionType.SET_PROMPT,
        payload: queryPayload.prompt,
      });
    }
  }, [defaultTimeRange, dispatch, metadata]);
};
